import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { CartProvider } from "@/context/CartContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const AppsPage = lazy(() => import("@/pages/AppsPage"));
const AppProductPage = lazy(() => import("@/pages/AppProductPage"));
const Reader = lazy(() => import("@/pages/Reader"));
const LibraryApp = lazy(() => import("@/pages/LibraryApp"));
const NotFound = lazy(() => import("@/pages/NotFound"));
import { ChatWidget } from "@/components/ChatWidget";
import { CartDrawer } from "@/components/CartDrawer";
import { BookRequestModal } from "@/components/BookRequestModal";
import { AmbientBackground } from "@/components/AmbientBackground";
import { CookieBanner } from "@/components/CookieBanner";
import { trackVisit } from "@/api";
import { trackEvent } from "@/lib/analytics";
import Home from "@/pages/Home";
const Blog = lazy(() => import("@/pages/Blog"));
const GiftCardsPage = lazy(() => import("@/pages/GiftCardsPage"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Legal = lazy(() => import("@/pages/Legal"));
const ProductPage = lazy(() => import("@/pages/ProductPage"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentCancel })));
const CustomerPanel = lazy(() => import("@/pages/CustomerPanel"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const Admin = lazy(() => import("@/pages/admin/Admin"));

function Layout() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const isReader = location.pathname.startsWith("/czytaj/") || location.pathname.startsWith("/biblioteka"); // aplikacja DigiLib i czytnik na pełnym ekranie — bez czatu i paska cookies

  useEffect(() => { trackVisit(); }, []);
  useEffect(() => { trackEvent("pageview"); }, [location.pathname]);

  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname]);

  // powrót z maila propozycji (rezygnacja) -> od razu otwórz czat z Booki, żeby dokończyć rozmowę
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("booki") !== "1") return;
    window.dispatchEvent(new Event("open-book-request"));
    params.delete("booki");
    const rest = params.toString();
    window.history.replaceState({}, "", location.pathname + (rest ? `?${rest}` : "") + location.hash);
  }, [location.search, location.pathname, location.hash]);

  return (
    <>
      <AmbientBackground />
      <div className="relative z-[1]">
        {!isAdmin && <Navbar />}
        <Suspense fallback={<div className="min-h-screen" />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/bony" element={<GiftCardsPage />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/produkt/:id" element={<ProductPage />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/regulamin" element={<Legal page="regulamin" />} />
          <Route path="/polityka-prywatnosci" element={<Legal page="polityka-prywatnosci" />} />
          <Route path="/wysylka" element={<Legal page="wysylka" />} />
          <Route path="/platnosci" element={<Legal page="platnosci" />} />
          <Route path="/zwroty" element={<Legal page="zwroty" />} />
          <Route path="/platnosc/sukces" element={<PaymentSuccess />} />
          <Route path="/platnosc/anulowana" element={<PaymentCancel />} />
          <Route path="/konto" element={<CustomerPanel />} />
          <Route path="/czytaj/:productId" element={<Reader />} />
          <Route path="/biblioteka" element={<LibraryApp />} />
          <Route path="/o-nas" element={<AboutPage />} />
          <Route path="/kontakt" element={<ContactPage />} />
          <Route path="/opinie" element={<ReviewsPage />} />
          <Route path="/aplikacje" element={<AppsPage />} />
          <Route path="/aplikacje/:id" element={<AppProductPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        {!isAdmin && <Footer />}
      </div>
      {!isAdmin && <CartDrawer />}
      <BookRequestModal />
      {!isReader && <ChatWidget />}
      {!isReader && <CookieBanner />}
      <Toaster position="top-center" theme="dark" richColors />
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-slate-950">
      <BrowserRouter>
        <LanguageProvider>
          <CartProvider>
            <Layout />
          </CartProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
